import { Text, Anchor } from '@mantine/core';
import { ReactNode } from 'react';
import { useExpandableText } from '@/hooks/useExpandableText';
import { BaseHighlightableTextProps, DEFAULT_HIGHLIGHT_STYLE, findMatches } from './shared';

const highlightPlainText = (content: string, term: string, style: React.CSSProperties): ReactNode[] => {
  const matches = findMatches(content, term);
  if (matches.length === 0) return [content];

  const parts: ReactNode[] = [];
  let currentIndex = 0;

  matches.forEach((match, matchIndex) => {
    // Add text before match
    if (match.start > currentIndex) {
      parts.push(content.slice(currentIndex, match.start));
    }

    // Add highlighted match
    const matchText = content.slice(match.start, match.end);
    parts.push(
      <span key={`highlight-${matchIndex}`} style={style}>
        {matchText}
      </span>
    );

    currentIndex = match.end;
  });

  // Add remaining text
  if (currentIndex < content.length) {
    parts.push(content.slice(currentIndex));
  }

  return parts;
};

export function HighlightablePlainText({
  text,
  searchTerm,
  maxLength = 1000,
  highlightStyle = DEFAULT_HIGHLIGHT_STYLE,
  ...textProps
}: BaseHighlightableTextProps) {
  // Handle text expansion/truncation
  const { shouldTruncate, toggleExpansion, truncatedContent, expanded } = useExpandableText({
    content: text,
    maxLength,
    searchTerm,
    isHtml: false,
  });

  return (
    <>
      <Text {...textProps}>{highlightPlainText(truncatedContent, searchTerm, highlightStyle)}</Text>

      {shouldTruncate && (
        <Anchor component="button" type="button" size="sm" onClick={toggleExpansion} style={{ marginTop: 4 }}>
          {expanded ? 'Show less' : 'Show more'}
        </Anchor>
      )}
    </>
  );
}
